import { useEffect, useState } from "react";
import { deviceTypes } from "../../../constants/deviceTypes.js";
import {
    Card,
    CardBody,
    Row,
    Col,
    Label
} from "reactstrap";
import { useStore } from "../../../utilities/appStore";
import {
    getDeviceSubscriptionDetails,
    assignUnassignLicense
} from "../../../utilities/orgPanelApiCalls";
import SubscriptionDisplay from "../../SubscriptionDisplay/SubscriptionDisplay";
import { subscriptionStatus } from "../../../constants/subscriptionStatus.js";
import { errorToast } from "../../../constants/toast";
import licenseAction from "../../../constants/licenseAction.js";
import Button from "../../UIButton/UIButton";
import Spinner from "../../UISpinner/UISpinner.js";

const SubscriptionStatus = ({ deviceId, refreshDevices, toastCallback }) => {
    const currentOrganization = useStore((state) => state.CurrentOrganizationId);
    const [licenseStatusObj, setLicenseStatusObj] = useState(null);
    const [isActionButtonDisabled, setActionButtonDisabled] = useState(false);

    useEffect(() => {
        async function getSubscriptionDetailsFromApi() {
            const result = await getDeviceSubscriptionDetails(
                deviceId,
                currentOrganization
            );
            setLicenseStatusObj(result?.data);
        }
        getSubscriptionDetailsFromApi();
    }, [deviceId, currentOrganization]);

    const clickActionBtnHandler = async (action) => {
        setActionButtonDisabled(true);
        const result = await assignUnassignLicense(
            deviceId,
            action,
            currentOrganization
        );
        setActionButtonDisabled(false);

        if (result.errorMsg) {
            toastCallback(result.errorMsg, errorToast);
            return;
        }

        refreshDevices();
    };

    const spinner = <Spinner text="Processing request..." />;

    if (isActionButtonDisabled || !licenseStatusObj) {
        return spinner;
    }

    const deviceType = licenseStatusObj.deviceType;
    const subStatus = licenseStatusObj.subscriptionStatus;

    let actionButton = null;

    if (deviceType !== deviceTypes.TraditionalDevice) {
        if (
            subStatus === subscriptionStatus.Inactive ||
            subStatus === subscriptionStatus.Expired
        ) {
            actionButton = (
                <Button
                    variant="primary"
                    size="small"
                    disabled={isActionButtonDisabled}
                    type="button"
                    onClick={() => clickActionBtnHandler(licenseAction.assign)}
                >
                    Assign License
                </Button>
            );
        } else if (
            subStatus === subscriptionStatus.Active &&
            !licenseStatusObj?.renewalDate
        ) {
            actionButton = (
                <Button variant="info" size="small" disabled type="button">
                    Locked
                </Button>
            );
        } else if (subStatus === subscriptionStatus.Active) {
            actionButton = (
                <Button
                    variant="danger"
                    size="small"
                    disabled={isActionButtonDisabled}
                    type="button"
                    onClick={() => clickActionBtnHandler(licenseAction.unassign)}
                >
                    Remove License
                </Button>
            );
        }
    }

    const hasExpiration = !!licenseStatusObj.expirationDate;
    const dateLabel = hasExpiration ? "Expiration Date:" : "Renewal Date:";
    const dateValue =
        licenseStatusObj.expirationDate ??
        licenseStatusObj.renewalDate ??
        "-";

    return (
        <Card className="my-2 subscription-status-card mr-4">
            <CardBody className="py-2 px-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label className="col-form-label mb-0">
                        Subscription Status
                    </Label>
                    {actionButton && <div>{actionButton}</div>}
                </div>

                <Row className="align-items-center mb-1">
                    <Col xs="12" md="6">
                        <Label className="col-form-label mb-0">
                            License Status:
                        </Label>
                    </Col>
                    <Col xs="12" md="6">
                        <SubscriptionDisplay
                            status={licenseStatusObj.subscriptionStatus}
                            type={deviceType}
                        />
                    </Col>
                </Row>

                <Row className="align-items-center">
                    <Col xs="12" md="6">
                        <Label className="col-form-label mb-0">
                            {dateLabel}
                        </Label>
                    </Col>
                    <Col xs="12" md="6">
                        {dateValue}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default SubscriptionStatus;
